import type { Question } from '../types';
import { userAuth } from './userAuth';

/**
 * Question Ordering Service
 *
 * Prioritizes media questions (with images/videos) for new users
 * across ALL topics
 *
 * This creates a better first impression and showcases app features
 * before users hit the paywall
 */

/**
 * Check if user is new (hasn't completed any tests)
 */
async function isNewUser(userId?: string): Promise<boolean> {
  try {
    if (!userId) {
      const user = userAuth.getCurrentUser();
      if (!user) return true; // Guest users are "new"
    }

    // For now, we'll use a simple heuristic:
    // Check localStorage for test completion
    const testsCompleted = localStorage.getItem('testsCompleted');
    return !testsCompleted || testsCompleted === '0';
  } catch {
    return false;
  }
}

/**
 * Reorder questions: media first, then text-only
 * Only applies to new users across ALL topics
 */
export async function orderQuestionsForNewUser(
  questions: Question[],
  topicId: string,
  userId?: string
): Promise<Question[]> {
  // Only apply to new users
  const newUser = await isNewUser(userId);
  if (!newUser) {
    return questions;
  }

  // Separate media and text-only questions
  const mediaQuestions = questions.filter(q => q.imageUrl && q.imageUrl.trim());
  const textOnlyQuestions = questions.filter(q => !q.imageUrl || !q.imageUrl.trim());

  // Return media first, then text (for all topics)
  return [...mediaQuestions, ...textOnlyQuestions];
}

/**
 * Get stats about media coverage for a topic
 */
export function getMediaCoverageStats(questions: Question[]): {
  total: number;
  withMedia: number;
  coverage: string;
} {
  const total = questions.length;
  const withMedia = questions.filter(q => q.imageUrl && q.imageUrl.trim()).length;
  const coverage = total > 0 ? `${Math.round((withMedia / total) * 100)}%` : '0%';

  return { total, withMedia, coverage };
}

/**
 * Log media-first ordering for analytics
 */
export function logMediaFirstOrdering(topicId: string, questionsCount: number, mediaCount: number): void {
  if (typeof window !== 'undefined' && (window as any).Canny) {
    try {
      (window as any).Canny('identify', {
        appID: 'app_id_here',
        customProperties: {
          mediaFirstOrderingApplied: true,
          topic: topicId,
          totalQuestions: questionsCount,
          mediaQuestions: mediaCount,
        },
      });
    } catch {
      // Ignore Canny logging errors
    }
  }

  // Console log for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log(`[MediaFirst] ${topicId}: ${mediaCount}/${questionsCount} with media`);
  }
}
